import { FC, useMemo } from "react";
import orderOptimizerHelper from "../../../common/order-optimizer.helper";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import TableButtonComponent from "../../../../../common/components/table/button/table-button.component";
import TableComponent, {
  TableProps,
} from "../../../../../common/components/table/table.component";
import uuidService from "../../../../../common/utils/uuid/uuid.service";
import { useAppContext } from "../../../../../context/app.context";
import OrderOptimizerRailyDetailsOrdersTableRow from "../common/types/order-optimizer-raily-details-orders-table-row";
import orderOptimizerRailyDetailsOrdersTableHelper from "./order-optimizer-raily-details-orders-table.helper";
import OrderOptimizerDetailsTableDotComponent from "../../../common/dot/order-optimizer-details-orders-table-dot.component";
import { OrderOptimizerRailyDetailsItemOrders } from "../common/types/order-optimizer-raily-details-item";
import orderOptimizerRailyDetailsHelper from "../order-optimizer-raily-details.helper";

type OrderOptimizerRailyDetailsOrdersTableProps = Pick<
  TableProps,
  "isError" | "isLoading"
> & {
  detailsOrderItems?: OrderOptimizerRailyDetailsItemOrders[];
  hiddenOrders?: number[];
  onOrderVisibilityClick: (id: number) => void;
};

const OrderOptimizerRailyDetailsOrdersTableComponent: FC<
  OrderOptimizerRailyDetailsOrdersTableProps
> = (props) => {
  const { selectedAppLanguage } = useAppContext();

  const columns = useMemo(() => {
    return orderOptimizerRailyDetailsOrdersTableHelper.getColumns();
  }, [selectedAppLanguage]);

  const createTableRow = (
    detailsOrderItems: OrderOptimizerRailyDetailsItemOrders,
    index: number,
    hiddenOrders: number[],
    humanIds: number[],
  ): OrderOptimizerRailyDetailsOrdersTableRow => {
    const distanceLabel = orderOptimizerRailyDetailsHelper.getDistanceLabel(
      detailsOrderItems.distance,
    );
    const distanceTitle = orderOptimizerRailyDetailsHelper.getDistanceLabel(
      detailsOrderItems.distance,
    );

    const totalTimeLabel = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsOrderItems.totalTime,
    );
    const totalTimeTitle = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsOrderItems.totalTime,
    );

    const haltingTimeLabel = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsOrderItems.haltingTime,
    );
    const haltingTimeTitle = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsOrderItems.haltingTime,
    );

    const isButtonVisible =
      props.detailsOrderItems!.length - props.hiddenOrders!.length >= 3 ||
      hiddenOrders.includes(humanIds[index]);
    const isOrderHidden = hiddenOrders.includes(humanIds[index]);
    const dotColor = isOrderHidden
      ? "#808080"
      : orderOptimizerHelper.getOrderColor(index);

    return {
      id: uuidService.generate(),
      className: isOrderHidden
        ? "order_optimizer_raily_details_orders_table__hidden_row"
        : undefined,
      value: {
        humanId: (
          <div className="order_optimizer_raily_details_orders_table__humanId">
            <OrderOptimizerDetailsTableDotComponent color={dotColor} />
            <span title={String(detailsOrderItems.humanId)}>
              {detailsOrderItems.humanId}
            </span>
          </div>
        ),
        cargoCompany: (
          <div title={detailsOrderItems.cargoCompany}>
            {detailsOrderItems.cargoCompany}
          </div>
        ),
        billingModel: (
          <div title={detailsOrderItems.billingModel}>
            {detailsOrderItems.billingModel}
          </div>
        ),
        taxiCorporation: (
          <div title={detailsOrderItems.taxiCorporation}>
            {detailsOrderItems.taxiCorporation}
          </div>
        ),
        totalTime: <div title={totalTimeTitle}>{totalTimeLabel}</div>,
        haltingTime: (
          <div
            title={haltingTimeTitle}
            className={detailsOrderItems.haltingTime < 0 ? "time_delayed" : ""}
          >
            {haltingTimeLabel}
          </div>
        ),
        distance: <div title={distanceTitle}>{distanceLabel}</div>,
        action: (
          <TableButtonComponent
            icon={faEye}
            type={"brand"}
            onClick={() =>
              props.onOrderVisibilityClick(detailsOrderItems.humanId)
            }
            isDisabled={!isButtonVisible}
          />
        ),
      },
    };
  };

  const rows: OrderOptimizerRailyDetailsOrdersTableRow[] = useMemo(() => {
    const detailsOrderItems = props.detailsOrderItems || [];
    const humanIds = detailsOrderItems.map((item) => item.humanId);

    return detailsOrderItems.map((item, index) =>
      createTableRow(item, index, props.hiddenOrders ?? [], humanIds),
    );
  }, [props.detailsOrderItems, props.hiddenOrders, selectedAppLanguage]);

  return (
    <TableComponent
      columns={columns}
      rows={rows}
      isLoading={props.isLoading}
      isError={props.isError}
    />
  );
};

export default OrderOptimizerRailyDetailsOrdersTableComponent;
